import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ArrowRight, Star, PawPrint, Users, Car, MessageCircle,
  TreePine, Flame, Heart, Mountain
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { IMAGES, ACCOMMODATIONS, TESTIMONIALS, SITE_INFO } from "@/lib/constants";
import { useLiveAccommodations } from "@/hooks/useLiveAccommodations";
import { GallerySlider } from "@/components/GallerySlider";
import { useExperiences } from "@/hooks/useExperiences";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const HeroSection = () => (
  <section data-testid="hero-section" className="relative h-screen min-h-[600px] flex items-end">
    <div className="absolute inset-0">
      <img
        src={IMAGES.hero}
        alt="Sunset over Lake Texoma with a peaceful pier"
        className="w-full h-full object-cover"
      />
      <div className="hero-overlay absolute inset-0" />
    </div>
    <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <p className="caption-text text-white/70 mb-4">Texoma Resorts / Serenity Village</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-white leading-none tracking-tight mb-4">
          Luxury Glamping, Cabins
          <br className="hidden md:block" />
          <span className="italic">&amp; Camping</span> Near Lake Texoma
        </h1>
        <p className="font-body text-base md:text-lg text-white/70 max-w-xl mb-8 leading-relaxed">
          Escape to Serenity Village at Texoma Resorts &mdash; where nature, adventure,
          and relaxation meet on the shores of Lake Texoma.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/bet-cabin">
            <Button data-testid="hero-book-stay" className="bg-accent hover:bg-accent/90 text-white font-body rounded-full px-6">
              Book Your Stay
            </Button>
          </Link>
          <Link to="/accommodations">
            <Button data-testid="hero-explore" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-body rounded-full px-6">
              Explore Accommodations
            </Button>
          </Link>
          <Link to="/camping">
            <Button data-testid="hero-camping" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-body rounded-full px-6">
              Book a Campsite
            </Button>
          </Link>
          <Link to="/experiences">
            <Button data-testid="hero-experiences" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-body rounded-full px-6">
              Add Adventure Experiences
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

const features = [
  { icon: PawPrint, title: "Pet Friendly", desc: "Bring your best friend along" },
  { icon: Users, title: "Families Welcome", desc: "Perfect for any family" },
  { icon: Car, title: "Free Parking", desc: "Private parking for guests" },
  { icon: MessageCircle, title: "24/7 Support", desc: "Hassle-free communication" },
];

const FeaturesSection = () => (
  <section data-testid="features-section" className="py-16 md:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {features.map((f, i) => (
          <motion.div key={f.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/5 flex items-center justify-center">
                <f.icon size={22} className="text-primary" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground mb-1">{f.title}</h3>
              <p className="font-body text-sm text-muted-foreground">{f.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const AccommodationsSection = () => {
  const { accommodations } = useLiveAccommodations();
  return (
  <section data-testid="accommodations-section" className="py-16 md:py-24">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div {...fadeUp} className="mb-12">
        <p className="caption-text text-accent mb-3">Our Accommodations</p>
        <h2 className="font-heading text-4xl md:text-6xl font-light text-foreground tracking-tight">
          Find Your Perfect Stay
        </h2>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {accommodations.map((acc, i) => (
          <motion.div key={acc.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
            <Link to={acc.slug}>
              <Card className="overflow-hidden border border-border/50 card-lift cursor-pointer group h-full">
                <div className="img-hover-zoom aspect-[4/3]">
                  <img src={acc.image} alt={acc.name} className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-5">
                  <Badge variant="secondary" className="mb-2 font-body text-xs">{acc.capacity}</Badge>
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-1">{acc.name}</h3>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed line-clamp-2">{acc.tagline}</p>
                  <div className="flex items-center gap-1 mt-3 text-accent font-body text-sm font-medium group-hover:gap-2 transition-all duration-200">
                    View Details <ArrowRight size={14} />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  );
};

const FeaturedCabinSection = () => {
  const { cabinListing } = useLiveAccommodations();
  return (
  <section data-testid="featured-cabin-section" className="py-16 md:py-24 bg-[#1A3C34]">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <motion.div {...fadeUp}>
          <p className="caption-text text-white/50 mb-3">Featured Accommodation</p>
          <h2 className="font-heading text-4xl md:text-6xl font-light text-white tracking-tight mb-6">
            {cabinListing?.title || "The Bet Cabin"}
          </h2>
          <p className="font-body text-base md:text-lg text-white/60 leading-relaxed mb-6">
            Our signature luxury cabin retreat on the shores of Lake Texoma.
            Fully furnished with modern amenities, a private deck, and stunning
            views of the surrounding nature. Perfect for couples and families alike.
          </p>
          <div className="flex flex-wrap gap-2 mb-8">
            {["Full Kitchen", "Private Deck", "AC & Heating", "Wi-Fi", "BBQ Grill", "Fire Pit"].map((a) => (
              <Badge key={a} variant="outline" className="border-white/20 text-white/70 font-body text-xs">
                {a}
              </Badge>
            ))}
          </div>
          <Link to="/bet-cabin">
            <Button data-testid="featured-cabin-cta" className="bg-accent hover:bg-accent/90 text-white font-body rounded-full px-8">
              Reserve Bet Cabin <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </motion.div>
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}>
          <div className="img-hover-zoom rounded-lg overflow-hidden">
            <img src={IMAGES.cabin1} alt="Bet Cabin at Lake Texoma" className="w-full aspect-[4/3] object-cover" />
          </div>
        </motion.div>
      </div>
    </div>
  </section>
  );
};

const experienceIcons = {
  adventure: Mountain,
  romantic: Heart,
  nature: TreePine,
};

const ExperiencesSection = () => {
  const experiences = useExperiences();
  return (
  <section data-testid="experiences-section" className="py-16 md:py-24">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div {...fadeUp} className="mb-12">
        <p className="caption-text text-accent mb-3">Add-On Experiences</p>
        <h2 className="font-heading text-4xl md:text-6xl font-light text-foreground tracking-tight">
          Adventure Awaits
        </h2>
      </motion.div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {experiences.slice(0, 3).map((exp, i) => {
          const Icon = experienceIcons[exp.category] || Flame;
          return (
            <motion.div key={exp.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
              <Card className="overflow-hidden border border-border/50 card-lift h-full">
                <div className="img-hover-zoom aspect-[16/10]">
                  <img src={exp.image} alt={exp.name} className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={16} className="text-accent" />
                    <span className="font-body text-xs text-muted-foreground uppercase tracking-wider">{exp.category}</span>
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-1">{exp.name}</h3>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed mb-3">{exp.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-2xl font-semibold text-primary">${exp.price}</span>
                    <span className="font-body text-xs text-muted-foreground">{exp.duration}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
      <motion.div {...fadeUp} className="text-center mt-10">
        <Link to="/experiences">
          <Button data-testid="view-all-experiences" variant="outline" className="font-body rounded-full px-8 border-primary text-primary hover:bg-primary hover:text-white">
            View All Experiences <ArrowRight size={16} className="ml-2" />
          </Button>
        </Link>
      </motion.div>
    </div>
  </section>
  );
};

const TestimonialsSection = () => {
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews((r.data || []).slice(0, 3))).catch(() => {});
  }, []);
  const items = reviews.length ? reviews : TESTIMONIALS;
  return (
  <section data-testid="testimonials-section" className="py-16 md:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div {...fadeUp} className="mb-12 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="caption-text text-accent mb-3">Guest Stories</p>
          <h2 className="font-heading text-4xl md:text-6xl font-light text-foreground tracking-tight">
            What Our Guests Say
          </h2>
        </div>
        <Link
          to="/reviews"
          data-testid="home-all-reviews-link"
          className="font-body text-sm text-primary hover:text-accent transition-colors inline-flex items-center gap-1"
        >
          Read All Reviews & Share Yours <ArrowRight size={15} />
        </Link>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {items.map((t, i) => (
          <motion.div key={t.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.15 }}>
            <Card className="h-full border border-border/50 p-6">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(t.rating)].map((_, j) => (
                  <Star key={`${t.id}-star-${j}`} size={14} className="fill-accent text-accent" />
                ))}
              </div>
              <p className="font-body text-sm text-foreground/80 leading-relaxed mb-6 italic">
                &ldquo;{t.text}&rdquo;
              </p>
              <div className="mt-auto">
                <p className="font-heading text-base font-semibold text-foreground">{t.name}</p>
                <p className="font-body text-xs text-muted-foreground">{t.location} &middot; {t.type}</p>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  );
};

const RetreatsSection = () => (
  <section data-testid="retreats-section" className="relative py-16 md:py-24">
    <div className="absolute inset-0">
      <img src={IMAGES.yoga} alt="Outdoor yoga retreat at Texoma" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/60" />
    </div>
    <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
      <motion.div {...fadeUp}>
        <p className="caption-text text-white/60 mb-3">Group Bookings & Retreats</p>
        <h2 className="font-heading text-4xl md:text-6xl font-light text-white tracking-tight mb-6">
          Book the Entire Resort
        </h2>
        <p className="font-body text-base md:text-lg text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed">
          Host your family reunion, spiritual retreat, yoga weekend, corporate getaway,
          or private celebration at Serenity Village. We accommodate groups up to {SITE_INFO.maxGroupSize} guests.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/retreats">
            <Button data-testid="retreats-cta" className="bg-accent hover:bg-accent/90 text-white font-body rounded-full px-8">
              Plan Your Retreat
            </Button>
          </Link>
          <Link to="/contact">
            <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-body rounded-full px-8">
              Contact Us
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

const NatureSection = () => (
  <section data-testid="nature-section" className="py-16 md:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div {...fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[IMAGES.homeDetail1, IMAGES.homeDetail2, IMAGES.homeDetail3, IMAGES.homeDetail4].map((img, i) => (
          <div key={img} className="img-hover-zoom rounded-lg overflow-hidden aspect-square">
            <img src={img} alt={`Resort detail ${i + 1}`} className="w-full h-full object-cover" />
          </div>
        ))}
      </motion.div>
      <motion.div {...fadeUp} className="text-center mt-10">
        <p className="caption-text text-accent mb-3">Life at the Lake</p>
        <h2 className="font-heading text-3xl md:text-5xl font-light text-foreground tracking-tight mb-4">
          Your Next Vacation Starts Here
        </h2>
        <p className="font-body text-base text-muted-foreground max-w-lg mx-auto mb-8">
          Just minutes from Lake Texoma and Eisenhower State Park, Serenity Village
          is your gateway to the best of Texas lakeside living.
        </p>
        <Link to="/gallery">
          <Button data-testid="view-gallery" variant="outline" className="font-body rounded-full px-8 border-primary text-primary hover:bg-primary hover:text-white">
            View Gallery <ArrowRight size={16} className="ml-2" />
          </Button>
        </Link>
      </motion.div>
    </div>
  </section>
);

const CTASection = () => (
  <section data-testid="cta-section" className="py-16 md:py-24 bg-secondary/30">
    <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
      <motion.div {...fadeUp}>
        <h2 className="font-heading text-4xl md:text-6xl font-light text-foreground tracking-tight mb-6">
          Ready to Escape?
        </h2>
        <p className="font-body text-base md:text-lg text-muted-foreground mb-8 leading-relaxed">
          Book your luxury stay, add adventure experiences, or reserve a campsite
          near Lake Texoma today.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/bet-cabin">
            <Button data-testid="cta-book-stay" className="bg-primary hover:bg-primary/90 text-white font-body rounded-full px-8 text-base">
              Book Your Stay
            </Button>
          </Link>
          <Link to="/experiences">
            <Button data-testid="cta-add-adventure" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white font-body rounded-full px-8 text-base">
              Add Adventure to Your Stay
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

export default function HomePage() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    name: "Texoma Resorts / Serenity Village",
    description: "Luxury glamping, cabins, and camping near Lake Texoma, Texas.",
    url: "https://www.texomaresorts.com",
    telephone: SITE_INFO.phoneRaw,
    email: SITE_INFO.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: "1642 FM1310",
      addressLocality: "Denison",
      addressRegion: "TX",
      postalCode: "75020",
      addressCountry: "US",
    },
    amenityFeature: [
      { "@type": "LocationFeatureSpecification", name: "Pet Friendly", value: true },
      { "@type": "LocationFeatureSpecification", name: "Free Parking", value: true },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Texoma Resorts | Luxury Glamping, Cabins & Camping Near Lake Texoma</title>
        <meta
          name="description"
          content="Escape to Serenity Village at Texoma Resorts. Luxury cabins, glamping safari tents, domes, camping, ATV adventures & speed boat rides near Lake Texoma, Texas."
        />
        <meta property="og:title" content="Texoma Resorts | Luxury Glamping, Cabins & Camping Near Lake Texoma" />
        <meta property="og:description" content="Escape to Serenity Village. Luxury stays, adventure experiences, and camping near Lake Texoma." />
        <meta property="og:image" content={IMAGES.hero} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <HeroSection />
      <FeaturesSection />
      <AccommodationsSection />
      <FeaturedCabinSection />
      <ExperiencesSection />
      <TestimonialsSection />
      <GallerySlider />
      <RetreatsSection />
      <NatureSection />
      <CTASection />
    </>
  );
}
